import { useMemo } from "react";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { Button, Card, ErrorMessage, Flex, Text } from "@jsq/ds/core";
import { FormTextInput, useFormContext } from "@jsq/ds/forms";
import { Colors, Sizes } from "@jsq/ds/styles";
import { useGetCountriesQuery, useGetTaxReportingDataQuery } from "~/graphql/supergraphHooks";
import { buildCountryCodeToNameMap } from "~/pages/Compliance/CaseDetailsDrawer/Documents/TaxForms/utils/countryCodeToName";
import { useCurrentCaseContext } from "~/pages/Compliance/context/CurrentCaseContext";
import { DOCUMENT_TYPE_MAPPING } from "../constants";
import { type DocumentType, type FormValues } from "../types";
import * as styles from "./styles_vanilla.css";
import { TaxDetailsTable } from "./TaxDetailsTable";
import {
  buildTaxFormUrl,
  getDocumentSubtypeLabel,
  getTaxDetailsFromMetadata,
  getTaxFormTypeFromDocument,
} from "./utils";

type TaxDetailsCardProps = {
  readonly: boolean;
  documentId: number | undefined;
  taxDocument?: DocumentType;
  investingProfileId?: number;
};

const TaxDetailsCard = ({
  readonly,
  documentId,
  taxDocument,
  investingProfileId,
}: TaxDetailsCardProps) => {
  const { values, errors, submitCount } = useFormContext<FormValues>();
  const history = useHistory();
  const location = useLocation();
  const match = useRouteMatch();
  const { caseId } = useCurrentCaseContext();
  const { data: countriesData } = useGetCountriesQuery();
  const countryCodeToName = useMemo(
    () => buildCountryCodeToNameMap(countriesData?.complianceCountries),
    [countriesData?.complianceCountries],
  );

  // Fetch tax reporting data for substantial owners / controlling persons
  const { data: taxReportingData } = useGetTaxReportingDataQuery({
    variables: { caseId, documentId },
    skip: !caseId || caseId <= 0 || !documentId,
    fetchPolicy: "cache-and-network",
  });

  const { documentType, secondaryDocumentType } = values;

  const documentCategory = useMemo(() => {
    if (documentType === DOCUMENT_TYPE_MAPPING.wSeriesTaxForm.enumValue) return "wSeries";
    if (documentType === DOCUMENT_TYPE_MAPPING.selfCertification.enumValue)
      return "selfCertification";
    return "none";
  }, [documentType]);

  const taxFormType = getTaxFormTypeFromDocument(documentCategory, secondaryDocumentType);
  const documentSubtypeLabel = getDocumentSubtypeLabel(documentCategory, secondaryDocumentType);

  const { taxDetails, hasDetails } = useMemo(() => {
    if (!taxFormType || taxFormType === "WITHHOLDING_STATEMENT") {
      return { taxDetails: [], hasDetails: false };
    }
    const details = getTaxDetailsFromMetadata(
      taxDocument?.metadata ?? null,
      taxFormType,
      countryCodeToName,
      taxReportingData?.taxReportingData ?? [],
    );
    return {
      taxDetails: details,
      hasDetails: details.some(
        (detail) => detail.hasValue && !detail.isSectionHeader && !!detail.displayValue,
      ),
    };
  }, [
    taxDocument?.metadata,
    taxFormType,
    countryCodeToName,
    taxReportingData?.taxReportingData,
  ]);

  const handleAddOrEditDetails = () => {
    if (!taxFormType || readonly) return;

    const url = buildTaxFormUrl({
      taxFormType,
      documentId,
      currentSearch: location.search,
      currentUrl: match.url,
      profileId: values.relatedProfile,
    });
    history.push(url);
  };

  // Only show for root profile (investing profile)
  const isRootProfile =
    investingProfileId !== undefined && values.relatedProfile === String(investingProfileId);

  if (!secondaryDocumentType || documentCategory === "none" || !isRootProfile) {
    return null;
  }

  // For withholding statement, show a simplified card with just entity name field
  if (taxFormType === "WITHHOLDING_STATEMENT") {
    return (
      <div className={styles.cardWrapper}>
        <Card>
          <Text variant="secondary-md" className={styles.cardTitle}>
            FATCA DETAILS
          </Text>
          {readonly ? (
            <Flex gap={Sizes.MD} className={styles.tableContainer}>
              <Text
                color={Colors.Neutral700}
                variant="secondary-md"
                className={styles.labelText}
              >
                Entity name
              </Text>
              <Text variant="regular-md">{values.entityName || "--"}</Text>
            </Flex>
          ) : (
            <FormTextInput isFullWidth name="entityName" label="Entity name" isRequired />
          )}
        </Card>
      </div>
    );
  }

  const cardTitle = documentCategory === "wSeries" ? "FATCA DETAILS" : "CRS DETAILS";
  const buttonLabel = hasDetails ? "Edit details" : "Add details";

  return (
    <div className={styles.cardWrapper}>
      <Card>
        <Flex justifyContent="space-between" alignItems="center">
          <Text variant="secondary-md">{cardTitle}</Text>
          {!readonly && (
            <Button displayAsLink variant="link" onClick={handleAddOrEditDetails}>
              {buttonLabel}
            </Button>
          )}
        </Flex>
        {hasDetails ? (
          <div className={styles.tableContainer}>
            <TaxDetailsTable taxDetails={taxDetails} />
          </div>
        ) : (
          <Flex flexDirection="column" gap={Sizes.MD}>
            <Text variant="regular-md" className={styles.descriptionText}>
              There are no details for this {documentSubtypeLabel}.
            </Text>
            {submitCount > 0 && errors.hasTaxDetails && (
              <ErrorMessage>{errors.hasTaxDetails}</ErrorMessage>
            )}
          </Flex>
        )}
      </Card>
    </div>
  );
};

export { TaxDetailsCard };
